// Extract the current edition's text; never paraphrase Scripture for the feed.
export function extractSocialVerses(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const seen = new Set();
  return Array.from(doc.querySelectorAll('em, td.vn')).flatMap(marker => {
    const refs = [...marker.textContent.matchAll(/(\d+):(\d+)/g)];
    if (!refs.length) return [];
    const [, chapter, verse] = refs[0];
    const id = `${chapter}-${verse}`;
    if (seen.has(id)) return [];
    const source = marker.matches('td') ? marker.closest('tr')?.querySelector('td.v')
      : marker.closest('.bible-reading-verse, .gal-verse, .hebrew-verse') || marker.nextElementSibling;
    if (!source) return [];
    const copy = source.cloneNode(true);
    copy.querySelectorAll('em, button, rt, rp, script, style').forEach(node => node.remove());
    const text = copy.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return [];
    seen.add(id);
    return [{ id, chapter: Number(chapter), verse: Number(verse),
      reference: refs.map(match => `${match[1]}:${match[2]}`).join(' / '), text }];
  });
}

export function shuffleSocialVerses(verses, previousId, random = Math.random) {
  const next = [...verses];
  for (let i = next.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  if (next.length > 1 && next[0].id === previousId) {
    [next[0], next[1]] = [next[1], next[0]];
  }
  return next;
}

// Keywords are used for interests and tags. QT excerpts come from commentary.
export const SOCIAL_TOPICS = [
  { id: 'love', label: '愛', pattern: /愛|慈しみ|\blov(?:e|es|ed|ing)\b/i },
  { id: 'hope', label: '希望', pattern: /希望|望み|\bhope\b/i },
  { id: 'peace', label: '平安', pattern: /平安|平和|安ら|\bpeace\b/i },
  { id: 'prayer', label: '祈り', pattern: /祈|\bpray(?:er|s|ed|ing)?\b/i },
  { id: 'light', label: '光', pattern: /光|\blight\b/i },
  { id: 'fear', label: '恐れ', pattern: /恐れ|恐れる|おそれ|\bfear\b|afraid/i },
  { id: 'wisdom', label: '知恵', pattern: /知恵|智慧|\bwisdom\b|\bwise\b/i },
  { id: 'joy', label: '喜び', pattern: /喜び|喜ぶ|よろこ|\bjoy\b|rejoic/i },
  { id: 'life', label: 'いのち', pattern: /いのち|命|\blife\b/i },
  { id: 'forgive', label: '赦し', pattern: /赦|ゆるし|forgiv/i },
];
export const SOCIAL_INTEREST_KEY = 'bible-social-interests-v1';
export function verseTopics(text) { return SOCIAL_TOPICS.filter(topic => topic.pattern.test(text)); }
export function readSocialInterests() {
  try {
    const stored = JSON.parse(localStorage.getItem(SOCIAL_INTEREST_KEY)) || {};
    return Object.fromEntries(SOCIAL_TOPICS.map(({ id }) => [id, Number.isFinite(stored[id]) ? Math.max(0, Math.min(30, stored[id])) : 0]));
  } catch { return {}; }
}
export function rememberSocialRead(text) {
  const next = readSocialInterests();
  for (const topic of verseTopics(text)) next[topic.id] = Math.min(30, (next[topic.id] || 0) + 1);
  try { localStorage.setItem(SOCIAL_INTEREST_KEY, JSON.stringify(next)); } catch { /* Reading works without storage. */ }
}
export function recommendSocialVerses(verses, interests, previousId, random = Math.random) {
  // Weighted random selection keeps room for discoveries beyond past interests.
  const ranked = verses.map(item => {
    const relevance = verseTopics(item.text).reduce((sum, topic) => sum + (interests[topic.id] || 0), 0);
    const weight = 1 + Math.min(4, relevance * 0.3);
    return { item, score: -Math.log(Math.max(Number.EPSILON, random())) / weight };
  }).sort((a, b) => a.score - b.score).map(({ item }) => item);
  if (ranked.length > 1 && ranked[0].id === previousId) [ranked[0], ranked[1]] = [ranked[1], ranked[0]];
  return ranked;
}
export function makeSocialCards(ordered, verses) {
  const positions = new Map(verses.map((item, index) => [item.id, index]));
  const mixed = [...ordered];
  // Spread topic matches through the feed.
  // Keep the recommended first verse and every source verse exactly once.
  for (let slot = 1; slot < mixed.length; slot += 6) {
    const candidate = mixed.findIndex((item, index) => index >= slot && verseTopics(item.text).length > 0);
    if (candidate < 0) break;
    [mixed[slot], mixed[candidate]] = [mixed[candidate], mixed[slot]];
  }
  return mixed.map((item, index) => {
    const topic = verseTopics(item.text)[0];
    const following = verses.slice(positions.get(item.id), positions.get(item.id) + 3).filter(next => next.chapter === item.chapter && next.book === item.book);
    if (item.collection !== 'apocrypha' && index % 3 === 1) return { item, kind: 'quote', topic };
    if (index % 3 === 2 && following.length > 1) return { item, kind: 'thread', verses: following };
    return { item, kind: 'verse' };
  });
}
